import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Users, Building2, MapPin, TrendingUp, DollarSign, 
  Activity, CheckCircle2, XCircle, ArrowLeft, Shield,
  Package, Coins
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [apiHealth, setApiHealth] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      const [statsRes, healthRes, packagesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/api-health`),
        axios.get(`${API}/admin/packages`)
      ]);
      
      setStats(statsRes.data);
      setApiHealth(healthRes.data);
      setPackages(packagesRes.data);
    } catch (error) {
      console.error("Erro ao carregar dados admin:", error);
      toast.error("Erro ao carregar dados do administrador");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-red-900/10 via-transparent to-transparent"></div>
      
      <div className="relative z-10 container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/login")}
            className="text-gray-400 hover:text-white"
            data-testid="btn-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8 text-red-600" />
            <span className="text-2xl font-bold">PAINEL ADMIN</span>
          </div>
          
          <div className="w-20"></div>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-[#1a1a1a] border border-gray-800">
            <TabsTrigger value="overview" data-testid="tab-overview">Visão Geral</TabsTrigger>
            <TabsTrigger value="packages" data-testid="tab-packages">Pacotes</TabsTrigger>
            <TabsTrigger value="apis" data-testid="tab-apis">APIs</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="bg-[#1a1a1a] border-gray-800" data-testid="stat-users">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-400">Total Usuários</CardTitle>
                  <Users className="w-4 h-4 text-red-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats?.total_users || 0}</div>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats?.active_users_week || 0} ativos esta semana
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-[#1a1a1a] border-gray-800" data-testid="stat-brands">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-400">Total Marcas</CardTitle>
                  <Building2 className="w-4 h-4 text-red-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats?.total_brands || 0}</div>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats?.active_users_today || 0} criadas hoje
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-[#1a1a1a] border-gray-800" data-testid="stat-tokens">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-400">Tokens</CardTitle>
                  <Coins className="w-4 h-4 text-red-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats?.tokens_sold || 0}</div>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats?.tokens_used || 0} usados
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-[#1a1a1a] border-gray-800" data-testid="stat-revenue">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-400">Receita Total</CardTitle>
                  <DollarSign className="w-4 h-4 text-red-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">
                    R$ {stats?.total_revenue_brl?.toFixed(2) || "0.00"}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats?.tokens_sold || 0} tokens vendidos
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-[#1a1a1a] border-gray-800">
                <CardHeader>
                  <CardTitle className="text-lg">Campanhas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-500">{stats?.total_campaigns || 0}</div>
                  <p className="text-sm text-gray-400">Total criadas</p>
                </CardContent>
              </Card>

              <Card className="bg-[#1a1a1a] border-gray-800">
                <CardHeader>
                  <CardTitle className="text-lg">Locais Monitorados</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-500">{stats?.total_locations || 0}</div>
                  <p className="text-sm text-gray-400">Geofences ativos</p>
                </CardContent>
              </Card>

              <Card className="bg-[#1a1a1a] border-gray-800">
                <CardHeader>
                  <CardTitle className="text-lg">Chamadas API</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm text-gray-400">Foursquare:</span>
                      <span className="text-lg font-bold text-red-500 ml-2">{stats?.api_calls_foursquare || 0}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-400">Gemini:</span>
                      <span className="text-lg font-bold text-red-500 ml-2">{stats?.api_calls_gemini || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Packages Tab */}
          <TabsContent value="packages" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {packages.map((pkg) => (
                <Card 
                  key={pkg.id}
                  className={`${
                    pkg.popular 
                      ? 'bg-gradient-to-br from-red-900/20 to-red-600/10 border-red-600/50' 
                      : 'bg-[#1a1a1a] border-gray-800'
                  }`}
                  data-testid={`package-${pkg.id}`}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>{pkg.name}</CardTitle>
                      {pkg.popular && (
                        <Badge className="bg-red-600">Popular</Badge>
                      )}
                    </div>
                    <CardDescription>{pkg.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="text-4xl font-bold text-red-500">
                        R$ {pkg.price_brl.toFixed(2)}
                      </div>
                      <p className="text-sm text-gray-400 mt-1">
                        {pkg.tokens} análises
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        R$ {(pkg.price_brl / pkg.tokens).toFixed(2)} por análise
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* APIs Tab */}
          <TabsContent value="apis" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Foursquare */}
              <Card className="bg-[#1a1a1a] border-gray-800" data-testid="api-foursquare">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Foursquare Places API</CardTitle>
                      <CardDescription>Busca de locais e geofencing</CardDescription>
                    </div>
                    {apiHealth?.foursquare?.configured ? (
                      <CheckCircle2 className="w-8 h-8 text-green-500" />
                    ) : (
                      <XCircle className="w-8 h-8 text-yellow-500" />
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <span className="text-sm text-gray-400">Status:</span>
                    <Badge 
                      className={`ml-2 ${
                        apiHealth?.foursquare?.configured 
                          ? 'bg-green-600' 
                          : 'bg-yellow-600'
                      }`}
                    >
                      {apiHealth?.foursquare?.status === 'configured' ? 'Configurada' : 'Modo Demo'}
                    </Badge>
                  </div>
                  {apiHealth?.foursquare?.key_preview && (
                    <div>
                      <span className="text-sm text-gray-400">Key:</span>
                      <code className="ml-2 text-xs bg-gray-900 px-2 py-1 rounded">
                        {apiHealth.foursquare.key_preview}
                      </code>
                    </div>
                  )}
                  <div className="text-xs text-gray-500">
                    {apiHealth?.foursquare?.configured 
                      ? '✅ Retornando locais reais'
                      : '⚠️ Usando locais mockados'
                    }
                  </div>
                </CardContent>
              </Card>

              {/* Gemini */}
              <Card className="bg-[#1a1a1a] border-gray-800" data-testid="api-gemini">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Gemini AI (Emergent)</CardTitle>
                      <CardDescription>Análise de marca e criativos</CardDescription>
                    </div>
                    {apiHealth?.gemini?.configured ? (
                      <CheckCircle2 className="w-8 h-8 text-green-500" />
                    ) : (
                      <XCircle className="w-8 h-8 text-red-500" />
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <span className="text-sm text-gray-400">Status:</span>
                    <Badge 
                      className={`ml-2 ${
                        apiHealth?.gemini?.configured 
                          ? 'bg-green-600' 
                          : 'bg-red-600'
                      }`}
                    >
                      {apiHealth?.gemini?.configured ? 'Configurada' : 'Não Configurada'}
                    </Badge>
                  </div>
                  {apiHealth?.gemini?.key_preview && (
                    <div>
                      <span className="text-sm text-gray-400">Key:</span>
                      <code className="ml-2 text-xs bg-gray-900 px-2 py-1 rounded">
                        {apiHealth.gemini.key_preview}
                      </code>
                    </div>
                  )}
                  <div className="text-xs text-gray-500">
                    {apiHealth?.gemini?.configured 
                      ? '✅ Emergent LLM Key ativa'
                      : '❌ Chave não encontrada'
                    }
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
